/**
 * Resolve notation + audio asset IDs for metadata-driven tune pages.
 * Covers modern `asset_ids` plus legacy extraction/import fields seen in content_pages.metadata.
 */

/**
 * Baked tune HTML often contains <img src="/api/assets/N/thumbnail"> while JSON metadata
 * never got `asset_ids.images` — merge these so notation + asset captions work.
 */
export function extractTuneNotationAssetIdsFromHtml(html) {
  if (!html || typeof html !== 'string') return [];
  const ids = [];
  const seen = new Set();
  const re = /<img\b[^>]*\bsrc=["']([^"']+)["']/gi;
  let m;
  while ((m = re.exec(html))) {
    const match = m[1].match(/\/api\/assets\/(\d+)\/thumbnail\b/i);
    if (!match) continue;
    const n = parseInt(match[1], 10);
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    ids.push(n);
  }
  return ids;
}

/**
 * Legacy pages: cranfordpub.com GIF/JPEG in an <img> when not using our asset API.
 */
const SKIP_EXTERNAL = /button[-_]?|buynow|buy[-_]now|paypal|addtocart|add[-_]to[-_]cart|checkout|banner|logo|spacer|pixel|tracker|icon|nav[-_]|menu[-_]|arrow|bullet|hr[-_]|divider|counter|visitor|hitcount|amazon|social|facebook|twitter|email[-_]btn|mailto|realg2|bhome\.gif|gifs_jpegs\/real|\/home\.gif/i;

export function extractTuneNotationExternalImageUrlFromHtml(html) {
  if (!html || typeof html !== 'string') return '';
  const re = /<img\b[^>]*\bsrc=["']([^"']+)["']/gi;
  let m;
  while ((m = re.exec(html))) {
    const s = m[1].trim();
    if (/\/api\/assets\//i.test(s)) continue;
    if (SKIP_EXTERNAL.test(s)) continue;
    if (
      /^https?:\/\/www\.cranfordpub\.com\//i.test(s) &&
      /\.(gif|jpe?g|png|webp)(\?[^#]*)?$/i.test(s)
    ) {
      // Only accept images from tune_gifs or notation directories
      if (/tune_gifs|\/notation\//i.test(s)) return s;
    }
  }
  return '';
}

export function collectTuneNotationAssetIds(m) {
  if (!m || typeof m !== 'object') return [];
  const out = [];
  const seen = new Set();
  const add = (v) => {
    const n = parseInt(String(v).trim(), 10);
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  };
  if (Array.isArray(m.asset_ids)) {
    m.asset_ids.forEach(add);
  }
  const assetIds =
    m.asset_ids && typeof m.asset_ids === 'object' && !Array.isArray(m.asset_ids) ? m.asset_ids : {};
  if (Array.isArray(assetIds.images)) assetIds.images.forEach(add);
  if (Array.isArray(m.gif_asset_ids)) m.gif_asset_ids.forEach(add);
  if (m.gif_asset_id != null && m.gif_asset_id !== '') add(m.gif_asset_id);
  if (m.notation_image_id != null && m.notation_image_id !== '') add(m.notation_image_id);
  if (m.notation_asset_id != null && m.notation_asset_id !== '') add(m.notation_asset_id);
  if (Array.isArray(m.images)) m.images.forEach(add);
  if (m.main_image_id != null && m.main_image_id !== '') add(m.main_image_id);
  if (Array.isArray(m.notation_images)) {
    for (const s of m.notation_images) {
      // Only thumbnail URLs denote notation images; /download is for audio (see collectTuneAudioIds).
      const match = String(s).match(/\/api\/assets\/(\d+)\/thumbnail\b/i);
      if (match) add(match[1]);
    }
  }
  return out;
}

/**
 * Mutates metadata so `asset_ids` is always `{ images: number[], audio: number[], ... }`.
 * Handles legacy flat-array `asset_ids`, `notation_images` API URL arrays, and fills empty
 * notation lists from other legacy fields (gif_asset_ids, etc.).
 */
export function normalizeTunePageMetadataAssetIds(meta) {
  if (!meta || typeof meta !== 'object') return;

  const ai = meta.asset_ids;
  const rest =
    ai && typeof ai === 'object' && !Array.isArray(ai) ? { ...ai } : {};
  delete rest.images;
  delete rest.audio;

  const toNumArr = (v) => {
    if (v == null) return [];
    return (Array.isArray(v) ? v : [v])
      .map((x) => parseInt(String(x), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
  };

  let images = [];
  let audio = [];

  if (Array.isArray(ai)) {
    images = toNumArr(ai);
  } else if (ai && typeof ai === 'object') {
    images = toNumArr(ai.images);
    audio = toNumArr(ai.audio);
  }

  if (Array.isArray(meta.notation_images)) {
    for (const s of meta.notation_images) {
      const match = String(s).match(/\/api\/assets\/(\d+)\/thumbnail\b/i);
      if (match) {
        const n = parseInt(match[1], 10);
        if (Number.isFinite(n) && n > 0 && !images.includes(n)) images.push(n);
      }
    }
  }

  const pushId = (v) => {
    const n = parseInt(String(v), 10);
    if (Number.isFinite(n) && n > 0 && !images.includes(n)) images.push(n);
  };
  if (Array.isArray(meta.images)) meta.images.forEach(pushId);
  if (meta.main_image_id != null && meta.main_image_id !== '') pushId(meta.main_image_id);

  if (images.length === 0) {
    images = collectTuneNotationAssetIds({ ...meta, asset_ids: { ...rest, images: [], audio } });
  }

  meta.asset_ids = { ...rest, images, audio };
}

/**
 * Static notation file from legacy `notation_image` when there are no asset IDs.
 * Returns absolute URL, site-relative path starting with /, or ''.
 */
export function tuneNotationLegacyImageUrl(m) {
  if (!m || typeof m !== 'object') return '';
  const direct = m.external_notation_url || m.notation_remote_url;
  if (direct && /^https?:\/\//i.test(String(direct).trim())) {
    return String(direct).trim();
  }
  const ni = m.notation_image;
  if (!ni || typeof ni !== 'object') return '';
  const u = ni.url || ni.path || '';
  const s = String(u).trim();
  if (!s) return '';
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  if (s.startsWith('/')) return s;
  return `/${s.replace(/^\/+/, '')}`;
}

/**
 * When `asset_ids.audio` is present (including `[]`), it is the sole source of truth.
 * Legacy `mp3_asset_id` / `mp3_asset_ids` / `audio` are only used when that list was never set.
 */
export function collectTuneAudioIds(m) {
  if (!m || typeof m !== 'object') return [];
  const out = [];
  const seen = new Set();
  const add = (v) => {
    const n = parseInt(String(v).trim(), 10);
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  };
  const assetIds =
    m.asset_ids && typeof m.asset_ids === 'object' && !Array.isArray(m.asset_ids) ? m.asset_ids : {};
  if (Array.isArray(assetIds.audio)) {
    assetIds.audio.forEach(add);
    return out;
  }
  if (m.mp3_asset_id != null && m.mp3_asset_id !== '') add(m.mp3_asset_id);
  if (Array.isArray(m.mp3_asset_ids)) m.mp3_asset_ids.forEach(add);
  else if (m.mp3_asset_ids != null && m.mp3_asset_ids !== '') {
    String(m.mp3_asset_ids)
      .split(/[,;\s]+/)
      .forEach((s) => add(s));
  }
  if (Array.isArray(m.audio)) m.audio.forEach(add);
  return out;
}

/** Keep legacy mp3 fields in sync with `asset_ids.audio`; strip stale IDs when audio is cleared. */
export function syncTuneAudioMetadataFields(meta) {
  if (!meta || typeof meta !== 'object') return;
  normalizeTunePageMetadataAssetIds(meta);
  const audio = Array.isArray(meta.asset_ids?.audio) ? meta.asset_ids.audio : [];
  if (audio.length === 0) {
    meta.mp3_asset_id = null;
    delete meta.mp3_asset_ids;
    delete meta.audio;
    meta.asset_ids = { ...(meta.asset_ids || {}), audio: [] };
    return;
  }
  meta.mp3_asset_id = audio[0];
  meta.mp3_asset_ids = audio.map(String).join(',');
  meta.asset_ids = { ...(meta.asset_ids || {}), audio };
}
